import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { EntityImageGallery } from '../../components/EntityImageGallery';
import api from '../../services/api';
import { API_BASE_URL } from '../../config/apiConfig';
import { deleteCatalogItemFile, updateCatalogItem } from '../../services/mutations';
import { CatalogItem } from './useData';
import { CatalogItemUploadSection } from './CatalogItemUploadSection';
import { ResizeImageModal } from './ResizeImageModal';

interface ProductPhotosFormBlockProps {
  itemId: number;
  item: CatalogItem;
}

/** Renders product photos upload + gallery for catalog item edit form. */
export const ProductPhotosFormBlock: React.FC<ProductPhotosFormBlockProps> = ({ itemId, item }) => {
  const { colors } = useTheme();
  const files = item?.FileAttachment ?? [];
  const [resizeFileId, setResizeFileId] = useState<number | null>(null);
  const resizeFile = resizeFileId ? files.find((f) => f.id === resizeFileId) : null;

  return (
    <>
      <h3 className={`text-base font-semibold mb-3 ${colors.text}`}>Product photos</h3>
      <p className={`text-sm mb-3 ${colors.textMuted}`}>
        When you have more than one image, click the star on the image to use in offers and PDFs. You can resize images to keep file size small.
      </p>
      <div className="space-y-4">
        <CatalogItemUploadSection itemId={itemId} />
        <EntityImageGallery
          files={files}
          getImageUrl={(fileId) => `${API_BASE_URL}/catalog-items/${itemId}/files/${fileId}`}
          fetchImage={async (fileId: number) => {
            const res = await api.get(`/catalog-items/${itemId}/files/${fileId}`, { responseType: 'blob' });
            return URL.createObjectURL(res.data as Blob);
          }}
          onDeleteFile={async (fileId) => {
            await deleteCatalogItemFile(itemId, fileId);
          }}
          primaryFileId={item.primaryFileAttachmentId ?? null}
          onSetPrimaryFile={async (fileId) => {
            await updateCatalogItem(itemId, { primaryFileAttachmentId: fileId });
          }}
          onResizeFile={(fileId) => setResizeFileId(fileId)}
        />
      </div>
      {resizeFileId != null && resizeFile && (
        <ResizeImageModal
          isOpen={true}
          onClose={() => setResizeFileId(null)}
          itemId={itemId}
          fileId={resizeFileId}
          fileName={resizeFile.fileName ?? null}
          fileSize={resizeFile.fileSize ?? null}
          wasPrimary={item.primaryFileAttachmentId === resizeFileId}
          onSuccess={() => setResizeFileId(null)}
        />
      )}
    </>
  );
};
